import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useComponentTracking } from '../../../utils/component-events'
import { brand } from '../../../redux/app-selectors'
import { FsCore } from '../../../types/fs-core'
import { setConfig } from '@uswitch/rvuconsent'
import { RegisterOptions, useForm, Ref, FieldValues, CustomElement } from 'react-hook-form'
import CallOut from '../../callout/ev-callout'
import ProfileInformation from '../form-helpers/profile-information'
import { maskPII } from '../../../utils/form'
import uuidv4 from '../../../utils/guid'
import { readCookieRaw } from '../../../utils/cookies'

export type RegisterTrack = (
  ref:
    | ((HTMLSelectElement & HTMLTextAreaElement & CustomElement<FieldValues> & HTMLInputElement) &
        Ref)
    | null,
) => void

interface SMEformProps {
  brand: 'money' | 'uswitch' | 'confused'
  sys: {
    id: string
  }
  name: string
}
const formName = 'Journey form'

const debounce: Record<string, number> = {}
const SMEform: React.FC<SMEformProps> = ({ brand }) => {
  const { register, errors, setValue, watch, unregister, control, setError, clearErrors } = useForm(
    {
      mode: 'onChange',
      defaultValues: {
        businessDetails: {
          businessType: 'limitedCompany',
        },
        directorDetails: {
          emailAddress: undefined,
        },
      },
    },
  )

  const businessDetails = watch('businessDetails')

  const [consents, setConsents] = useState({ marketing: true })
  const [landingPage, setLandingPage] = useState<string | null>(null)

  const trackComponent: (
    eventName: string,
    subEventName: string,
    payload: Record<string, any>,
  ) => void = useComponentTracking()

  const piiData = [
    'emailAddress',
    'address_line_1',
    'address_line_2',
    'postal_code',
    'locality',
    'country',
  ]

  const registerWithTrack = (validation: RegisterOptions, detail: string): RegisterTrack => {
    return register({
      ...validation,
      validate: (value: any) => {
        clearTimeout(debounce[detail])
        debounce[detail] = setTimeout(() => {
          trackComponent('Form Event', 'formFieldInteracted', {
            formStep: detail,
            selected: piiData.includes(detail) ? 'untracked' : value,
          })
        }, 1000)
        return true
      },
    })
  }

  useEffect(() => {
    setConfig({
      apiPath: window.location.href.includes('staging')
        ? 'https://www.staging.money.co.uk/consent'
        : 'https://www.money.co.uk/consent',
      clientId: 'fs-core',
      brand: 'money',
    })
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location?.search)
      const landingPageParam = urlSearchParams.get('landingPage')
      if (landingPageParam) {
        setLandingPage(landingPageParam)
        sessionStorage.setItem('landingPage', landingPageParam)
      }
    }
  }, [])

  const handleSubmit = async (finalBody: Object) => {
    const res = await fetch(
      `${__FS_API_URL__}/form/sme?brand=${brand}&deviceId=${readCookieRaw(
        'uscd',
      )}&landingPage=${landingPage}&requestId=${uuidv4()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(finalBody),
      },
    )
    await Promise.all([
      trackComponent('Form Event', 'sectionCompleted', {
        SectionName: 'Profile Information',
        FormName: formName,
        FieldData: maskPII(finalBody, ['businessAddress', 'email']),
        status: res.ok ? 'Success' : 'Failed',
      }),
      trackComponent('Form Event', 'formSubmitted', {
        formName: formName,
      }),
    ]).catch(() => {
      // eslint-disable-next-line no-console
      console.log('SME Journey form tracking failed')
    })
    window.location.href = '/business/journey/results'
  }

  return (
    <div className='SMEform'>
      <h3>Tell us about your business</h3>
      <form>
        <ProfileInformation
          registerWithTrack={registerWithTrack}
          register={register}
          setValue={setValue}
          unregister={unregister}
          businessDetails={watch('businessDetails')}
          directorDetails={watch('directorDetails')}
          hideButton={false}
          consents={consents}
          setConsents={setConsents}
          setError={setError}
          clearErrors={clearErrors}
          errors={errors}
          brand={brand}
          formName={formName}
          ctaText='Submit'
          landingPage={landingPage}
          handleSubmit={handleSubmit}
          control={control}
          consentSource='profiling-journey'
        />
      </form>
      {businessDetails.businessType === 'limitedCompany' && businessDetails.turnover >= 100000 && (
        <div className='SMEform__callout'>
          <CallOut
            text={
              "The information you provide will be used for a 'soft credit search', visible to you but not to lenders and it will have no impact on your credit score."
            }
            bold={true}
          />
        </div>
      )}
    </div>
  )
}

export default connect((state: FsCore.State, props: any) => {
  return {
    brand: brand(state),
    ...props,
  }
})(SMEform)
